import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { useAppContext } from 'providers/AppProvider';
import ActionButton from '../../common/ActionButton';
import {
  addOfficialRate, editOfficialRate, listOfficialRates,
  listOfficialRoles, removeOfficialRate
} from '../../../lib/api/competition';

const errorMessage = error =>
  error.body?.errors?.map(field => field.msg).join(' ') ||
  error.body?.message || 'Zahtjev nije uspio. Pokušajte ponovo.';

const byOrder = (a, b) => Number(a.sort_order) - Number(b.sort_order);

export default function OfficialRates({ competition, onClose }) {
  const { userApp } = useAppContext();
  const canManage = ['admin', 'operator'].includes(userApp?.role_type_id);
  const competitionId = Number(competition.competitions_id);
  const validCompetition = Number.isSafeInteger(competitionId) && competitionId > 0;
  const [roles, setRoles] = useState([]);
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [deleteRate, setDeleteRate] = useState(null);

  const load = useCallback(async () => {
    if (!validCompetition) {
      setLoadError('Prvo sačuvajte takmičenje da biste unijeli cjenovnik.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError('');
    try {
      const [nextRoles, nextRates] = await Promise.all([
        listOfficialRoles(), listOfficialRates(competitionId)
      ]);
      setRoles([...nextRoles].sort(byOrder));
      setRates([...nextRates].sort(byOrder));
    } catch (err) {
      setLoadError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [competitionId, validCompetition]);

  useEffect(() => { load(); }, [load]);

  const openForm = rate => {
    setForm(rate ? {
      id: rate.id,
      match_official_role_id: String(rate.match_official_role_id),
      amount_eur: rate.amount_eur
    } : { match_official_role_id: '', amount_eur: '' });
    setErrors({});
    setError('');
  };

  const save = async event => {
    event.preventDefault();
    if (!canManage || busy || !validCompetition) return;
    const amount = String(form.amount_eur).trim();
    const nextErrors = {};
    if (!form.match_official_role_id) nextErrors.role = 'Izaberite ulogu.';
    if (!/^\d+(\.\d{1,2})?$/.test(amount) || Number(amount) > 99999999.99) {
      nextErrors.amount = 'Unesite iznos od 0 do 99999999.99 sa najviše dvije decimale.';
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    setBusy(true);
    setError('');
    try {
      const params = {
        match_official_role_id: Number(form.match_official_role_id),
        amount_eur: Number(amount).toFixed(2)
      };
      if (form.id != null) await editOfficialRate(competitionId, form.id, params);
      else await addOfficialRate(competitionId, params);
      setForm(null);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!canManage || busy || !validCompetition) return;
    setBusy(true);
    setError('');
    try {
      await removeOfficialRate(competitionId, deleteRate.id);
      setDeleteRate(null);
      await load();
    } catch (err) {
      setError(errorMessage(err));
      setDeleteRate(null);
    } finally {
      setBusy(false);
    }
  };

  // Keep an existing inactive role selectable when editing its rate.
  const availableRoles = roles.filter(role =>
    Number(role.is_active) === 1 ||
    (form?.id != null && String(role.id) === form.match_official_role_id)
  );

  return (
    <Modal show onHide={busy ? undefined : onClose} size="lg" backdrop={busy ? 'static' : true}>
      <Modal.Header closeButton={!busy}>
        <Modal.Title>Cjenovnik službenih lica — {competition.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" role="alert">{error}</Alert>}
        {loadError && (
          <Alert variant="danger" role="alert">
            {loadError}
            {validCompetition && <Button variant="link" onClick={load}>Pokušaj ponovo</Button>}
          </Alert>
        )}
        {loading ? <div className="text-center"><Spinner animation="border" role="status"><span className="visually-hidden">Učitavanje cjenovnika</span></Spinner></div> : !loadError && (
          <>
            {canManage && !form && !deleteRate && (
              <Button className="mb-3" onClick={() => openForm(null)} disabled={busy}>Unos stavke</Button>
            )}
            <Table responsive>
              <thead><tr><th>Uloga</th><th className="text-end">Iznos (EUR)</th>{canManage && <th className="text-end">Akcija</th>}</tr></thead>
              <tbody>
                {rates.length === 0 && <tr><td colSpan={canManage ? 3 : 2} className="text-center text-muted">Nema unesenih stavki cjenovnika.</td></tr>}
                {rates.map(rate => (
                  <tr key={rate.id}>
                    <td>{rate.name}</td>
                    <td className="text-end">{Number(rate.amount_eur).toFixed(2)}</td>
                    {canManage && <td className="text-end text-nowrap">
                      <ActionButton icon="edit" title="Izmijeni" aria-label="Izmijeni" variant="action" className="p-0 me-2" disabled={busy || !!deleteRate} onClick={() => openForm(rate)} />
                      <ActionButton icon="trash-alt" title="Izbriši" aria-label="Izbriši" variant="action" className="p-0 me-2" disabled={busy || !!deleteRate} onClick={() => { setForm(null); setError(''); setDeleteRate(rate); }} />
                    </td>}
                  </tr>
                ))}
              </tbody>
            </Table>
            {canManage && form && (
              <Form noValidate onSubmit={save} className="border rounded p-3">
                <h6>{form.id != null ? 'Izmjena stavke' : 'Unos stavke'}</h6>
                <Row className="g-3 mb-3">
                  <Form.Group as={Col} md={8} controlId="official-rate-role">
                    <Form.Label>Uloga</Form.Label>
                    <Form.Select value={form.match_official_role_id} disabled={busy} isInvalid={!!errors.role} onChange={e => setForm({ ...form, match_official_role_id: e.target.value })}>
                      <option value="">Izaberite</option>
                      {availableRoles.map(role => <option key={role.id} value={role.id}>{role.name}</option>)}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">{errors.role}</Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group as={Col} md={4} controlId="official-rate-amount">
                    <Form.Label>Iznos (EUR)</Form.Label>
                    <Form.Control type="text" inputMode="decimal" placeholder="60.00" value={form.amount_eur} disabled={busy} isInvalid={!!errors.amount} onChange={e => setForm({ ...form, amount_eur: e.target.value })} />
                    <Form.Control.Feedback type="invalid">{errors.amount}</Form.Control.Feedback>
                  </Form.Group>
                </Row>
                <Button type="submit" disabled={busy}>{busy ? 'Čuvanje…' : 'Sačuvaj'}</Button>
                <Button variant="secondary" className="ms-2" disabled={busy} onClick={() => setForm(null)}>Odustani</Button>
              </Form>
            )}
            {canManage && deleteRate && (
              <Alert variant="warning">
                <p>Da li ste sigurni da želite da izbrišete cijenu za ulogu „{deleteRate.name}“?</p>
                <Button variant="danger" disabled={busy} onClick={remove}>{busy ? 'Brisanje…' : 'Potvrdi brisanje'}</Button>
                <Button variant="secondary" className="ms-2" disabled={busy} onClick={() => setDeleteRate(null)}>Odustani</Button>
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer><Button variant="secondary" onClick={onClose} disabled={busy}>Zatvori</Button></Modal.Footer>
    </Modal>
  );
}
